// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useModifyAppRewardConfig } from '@hooks/mutations/registry/onboarding/useModifyAppRewardConfig';
import React from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { AppRewardConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/AppReward';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput, { TextInputLabel } from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import TemplateContract from '../../../utils/TemplateContract';
import { fmt } from '../../../utils/common';
import { isPositiveFloat } from '../../../utils/isPositiveFloat';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  row: TemplateContract<AppRewardConfiguration>;
  disableDialog?: boolean;
};

export const UpdateWeightDialog = ({ disableDialog, row, open, onClose }: DialogProps) => {
  const [weight, setWeight] = React.useState<string>(
    fmt(row.payload.details.operatorAppRewardBeneficiary.weight, 2) || '',
  );
  const [beneficiary, setBeneficiary] = React.useState(
    row.payload.details.operatorAppRewardBeneficiary.beneficiary || '',
  );
  const modify = useModifyAppRewardConfig();

  // Check if service is already requested or provided
  const { dso } = useParties();
  const valuesUnchanged =
    row.payload.details.operatorAppRewardBeneficiary.beneficiary === beneficiary &&
    fmt(row.payload.details.operatorAppRewardBeneficiary.weight, 2) === weight;
  const [showError, setShowError] = React.useState(false);

  const submit = async () => {
    await modify({
      cid: row.contractId,
      argument: {
        details: {
          dso,
          operatorAppRewardBeneficiary: {
            weight,
            beneficiary,
          },
        },
      },
    });
    onClose();
  };

  const isLoading = false;
  if (isLoading) return null;

  const disableSubmit = valuesUnchanged || showError || weight.length === 0;
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowError(false);
    const w = e.target.value;
    if (isPositiveFloat(w)) {
      setWeight(w);
      const decimal = parseFloat(w);
      if (decimal > 1) {
        setShowError(true);
      }
    }
  };
  return (
    <FormDialog
      open={open}
      title="Provider Service"
      onCancel={onClose}
      onSubmit={submit}
      submitName="Update"
      disableDialog={disableDialog}
      isSubmitDisabled={disableSubmit}
    >
      <TextInput enableTopLabel label="Beneficiary" value={beneficiary} setValue={setBeneficiary} />
      <>
        <Box sx={{ mb: 1 }}>
          <TextInputLabel label="Weight" />
          <Typography variant="caption">
            Value must be greater than 0, and less than or equal to 1
          </Typography>
        </Box>
        <TextField
          placeholder="weight"
          variant="outlined"
          fullWidth
          size="small"
          value={weight}
          onChange={onChange}
        />
      </>
      {showError && (
        <Typography variant="caption" color="error">
          Value is greater than 1. Reduce the weight.
        </Typography>
      )}
    </FormDialog>
  );
};
